import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { GameInfo, MatchFound, QueueStatus } from '@bn/shared';
import { api } from '@/lib/api';
import { ask, useSocket } from '@/lib/socket';
import { Alert, Badge, Button, Card, Spinner } from '@/components/ui';

export function LobbyPage() {
  const { socket, connected } = useSocket();
  const navigate = useNavigate();
  const [games, setGames] = useState<GameInfo[]>([]);
  const [queue, setQueue] = useState<QueueStatus | null>(null);
  const [activeMatchId, setActiveMatchId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void api
      .get<{ games: GameInfo[] }>('/api/games')
      .then((r) => setGames(r.games))
      .catch(() => setError('Impossible de charger la liste des jeux.'));
    void api
      .get<{ matchId: string | null }>('/api/matches/active')
      .then((r) => setActiveMatchId(r.matchId))
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    if (!socket) return;

    const onFound = (payload: MatchFound) => {
      setQueue(null);
      navigate(`/partie/${payload.matchId}`);
    };
    const onQueue = (status: QueueStatus) => {
      setQueue(status.position === null ? null : status);
    };

    socket.on('match:found', onFound);
    socket.on('queue:status', onQueue);
    return () => {
      socket.off('match:found', onFound);
      socket.off('queue:status', onQueue);
    };
  }, [socket, navigate]);

  const join = useCallback(
    async (gameId: string) => {
      if (!socket) return;
      setError(null);
      setBusy(true);
      try {
        const status = await ask<QueueStatus>(socket, 'queue:join', { gameId });
        setQueue(status.position === null ? null : status);
      } catch (cause) {
        setError(cause instanceof Error ? cause.message : 'Erreur inconnue.');
      } finally {
        setBusy(false);
      }
    },
    [socket],
  );

  const startSolo = useCallback(
    async (gameId: string, level: string | undefined, side: string | undefined) => {
      if (!socket) return;
      setError(null);
      setBusy(true);
      try {
        const result = await ask<{ matchId: string }>(socket, 'match:solo', {
          gameId,
          ...(level ? { level } : {}),
          ...(side ? { side } : {}),
        });
        navigate(`/partie/${result.matchId}`);
      } catch (cause) {
        setError(cause instanceof Error ? cause.message : 'Erreur inconnue.');
      } finally {
        setBusy(false);
      }
    },
    [socket, navigate],
  );

  const leave = useCallback(async () => {
    if (!socket) return;
    setBusy(true);
    try {
      await ask(socket, 'queue:leave', {});
      setQueue(null);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Erreur inconnue.');
    } finally {
      setBusy(false);
    }
  }, [socket]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-100">Salon</h1>
        <p className="text-sm text-slate-400">
          Choisissez un jeu : vous serez apparié au premier adversaire disponible.
        </p>
      </div>

      {error && <Alert>{error}</Alert>}

      {activeMatchId && (
        <Card className="border-sky-500/40 bg-sky-950/30">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="font-semibold text-sky-200">Vous avez une partie en cours.</p>
              <p className="text-sm text-slate-400">
                Reprenez-la là où vous l’avez laissée.
              </p>
            </div>
            <Button onClick={() => navigate(`/partie/${activeMatchId}`)}>
              Reprendre la partie
            </Button>
          </div>
        </Card>
      )}

      {!connected && <Alert type="info">Connexion au serveur de jeu en cours…</Alert>}

      <div className="grid gap-4 md:grid-cols-2">
        {games.length === 0 && <Spinner label="Chargement des jeux…" />}
        {games.map((game) => (
          <GameCard
            key={game.id}
            game={game}
            queue={queue?.gameId === game.id ? queue : null}
            busy={busy}
            connected={connected}
            onJoin={() => void join(game.id)}
            onLeave={() => void leave()}
            onSolo={(level, side) => void startSolo(game.id, level, side)}
          />
        ))}
      </div>
    </div>
  );
}

function GameCard({
  game,
  queue,
  busy,
  connected,
  onJoin,
  onLeave,
  onSolo,
}: {
  game: GameInfo;
  queue: QueueStatus | null;
  busy: boolean;
  connected: boolean;
  onJoin: () => void;
  onLeave: () => void;
  onSolo: (level: string | undefined, side: string | undefined) => void;
}) {
  const [level, setLevel] = useState(game.soloLevels[0]?.id ?? '');
  const [side, setSide] = useState(game.soloSides[0]?.id ?? '');
  const soloAvailable = game.soloLevels.length > 0;

  return (
    <Card title={game.name}>
      <p className="mb-4 text-sm text-slate-400">{game.description}</p>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Badge>{game.minPlayers} joueurs</Badge>
        {soloAvailable && <Badge tone="info">Mode solo disponible</Badge>}
        {queue && (
          <Badge tone="info">
            En attente — position {queue.position} sur {queue.size}
          </Badge>
        )}
      </div>

      {queue ? (
        <Button variant="secondaire" disabled={busy} onClick={onLeave}>
          Annuler la recherche
        </Button>
      ) : (
        <Button disabled={busy || !connected} onClick={onJoin}>
          Chercher un adversaire
        </Button>
      )}

      {soloAvailable && !queue && (
        <div className="mt-4 border-t border-slate-700/60 pt-4">
          <p className="mb-2 text-sm font-medium text-slate-300">Contre l’ordinateur</p>
          <div className="flex flex-wrap items-end gap-2">
            <label className="text-xs text-slate-400">
              <span className="mb-1 block">Niveau</span>
              <select
                value={level}
                onChange={(event) => setLevel(event.target.value)}
                className="rounded-lg border border-slate-600 bg-slate-950/70 px-2 py-1.5 text-sm text-slate-100"
              >
                {game.soloLevels.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            {game.soloSides.length > 0 && (
              <label className="text-xs text-slate-400">
                <span className="mb-1 block">Camp</span>
                <select
                  value={side}
                  onChange={(event) => setSide(event.target.value)}
                  className="rounded-lg border border-slate-600 bg-slate-950/70 px-2 py-1.5 text-sm text-slate-100"
                >
                  {game.soloSides.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            )}

            <Button
              variant="secondaire"
              disabled={busy || !connected}
              onClick={() => onSolo(level || undefined, side || undefined)}
            >
              Jouer
            </Button>
          </div>
          <p className="mt-2 text-xs text-slate-500">
            Les parties contre l’ordinateur ne comptent pas au classement.
          </p>
        </div>
      )}
    </Card>
  );
}
